import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { callAIToolsStreaming } from '@4cm/core/ai';
import { buildBrainContext } from '@4cm/brain/buildBrainContext';
import { makeBrainDispatcher, BRAIN_TOOLS } from '@4cm/brain/brainTools';
import { commitDraft } from '@4cm/brain/draftCommit';
import type { ScreenContext } from '@4cm/brain/brainPrompt';
import { useAuth } from '../contexts/AuthContext';
import { supabase, isPlaceholderSupabase } from './supabase';
import {
  gatherOfflinePriors,
  synthesizeOfflineDraft,
  synthesizeOfflineTacticsDraft,
} from './brainOffline';
import { useCoachReviewMode } from '../hooks/useCoachReviewMode';
import { isObviousFeedback } from './coachFeedback';

export interface BrainTurn {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  draft?: { type: string; payload: unknown } | null;
  status?: 'pending' | 'approved' | 'dismissed';
}

interface UseBrainOptions {
  teamId: string | null;
  screen?: ScreenContext;
  /**
   * Lazy read of the active tactics layout. The Brain's tactics
   * tools (critique_tactics_pattern in Phase 1) need to see the
   * current board state without the model echoing the full payload
   * through tool args. The hook propagates this to the dispatcher
   * via BrainToolCtx; callers register it via TacticsBrainContext
   * when the coach is on a tactics surface.
   */
  getTacticsLayout?: () => import('@4cm/core/tactics').TacticsLayout | null;
  /**
   * Surface-provided commit handler for `draft_tactics_pattern`
   * drafts. The generic `commitDraft` doesn't know about
   * localStorage-backed tactics persistence, so tactics pages
   * supply this via `useTacticsCommitForBoard`. Returns `{ ok, ... }`
   * mirroring the rest of the commit pipeline.
   */
  onCommitTacticsDraft?: (
    payload: import('./brainTactics').TacticsDraftPayload,
  ) => import('./brainTactics').TacticsCommitResult;
  /**
   * Surface-provided commit handler for `draft_player_briefs` drafts.
   * Mirrors `onCommitTacticsDraft` — writes the brief set into the
   * active layout's `playerBriefs` slot via localStorage. Like the
   * pattern draft, no coach auth needed.
   */
  onCommitPlayerBriefsDraft?: (
    payload: import('./brainTactics').PlayerBriefsDraftPayload,
  ) => import('./brainTactics').TacticsCommitResult;
}

const DEFAULT_SCREEN: ScreenContext = { kind: 'unknown' };

type DraftStatus = NonNullable<BrainTurn['status']>;

interface ConversationRow {
  id: string;
}

interface MessageRow {
  id: string;
  role: string;
  content: string | null;
  draft_payload: unknown | null;
  draft_status: string | null;
}

interface InsertedRow {
  id: string;
  created_at?: string | null;
}

interface SavedToolTrace {
  name: string;
  input: unknown;
  errored: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function serializeDraft(type: string, payload: unknown): Record<string, unknown> {
  return isRecord(payload) ? { __type: type, ...payload } : { __type: type, value: payload };
}

function parseDraft(payload: unknown): BrainTurn['draft'] {
  if (!isRecord(payload) || typeof payload.__type !== 'string') return null;
  const draftPayload = Object.fromEntries(
    Object.entries(payload).filter(([key]) => key !== '__type'),
  );
  return { type: payload.__type, payload: draftPayload };
}

function parseStatus(value: string | null): DraftStatus | undefined {
  if (value === 'dismissed') return 'dismissed';
  if (value === 'approved' || value === 'edited_approved') return 'approved';
  if (value === 'pending') return 'pending';
  return undefined;
}

function friendlyBrainError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (err instanceof TypeError && /fetch/i.test(msg)) {
    return "Brain is offline — couldn't reach the server. Try again in a moment.";
  }
  if (/NetworkError|Failed to fetch|ECONNREFUSED|ENOTFOUND/i.test(msg)) {
    return "Brain is offline — couldn't reach the server. Try again in a moment.";
  }
  return "Brain hit a snag. Try again, or refresh if it sticks.";
}

function rowToTurn(row: MessageRow): BrainTurn | null {
  if (row.role !== 'user' && row.role !== 'assistant') return null;
  const draft = parseDraft(row.draft_payload);
  return {
    id: row.id,
    role: row.role,
    text: row.content ?? '',
    draft,
    status: draft ? parseStatus(row.draft_status) ?? 'pending' : undefined,
  };
}

/**
 * Minimal Brain client for Phase 6. Persists one active conversation per
 * coach/team and rehydrates its messages on reload. Drafts can be approved
 * (committed via `commitDraft`) or dismissed.
 */
export function useBrain({
  teamId,
  screen,
  getTacticsLayout,
  onCommitTacticsDraft,
  onCommitPlayerBriefsDraft,
}: UseBrainOptions) {
  const { user } = useAuth();
  const coachId = user?.id ?? null;
  const reviewMode = useCoachReviewMode();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [turns, setTurns] = useState<BrainTurn[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (!coachId) {
      setConversationId(null);
      setTurns([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        let query = supabase
          .from('coach_conversations')
          .select('id')
          .eq('coach_id', coachId)
          .eq('archived', false)
          .order('last_message_at', { ascending: false })
          .limit(1);
        query = teamId ? query.eq('team_id', teamId) : query.is('team_id', null);

        const { data: conversation, error: conversationError } = await query.maybeSingle();
        if (cancelled) return;
        if (conversationError) {
          console.warn('[web2 brain] conversation load failed', conversationError.message);
          setConversationId(null);
          setTurns([]);
          return;
        }

        const conversationRow = conversation as ConversationRow | null;
        if (!conversationRow) {
          setConversationId(null);
          setTurns([]);
          return;
        }

        const { data: messages, error: messagesError } = await supabase
          .from('coach_messages')
          .select('id, role, content, draft_payload, draft_status')
          .eq('conversation_id', conversationRow.id)
          .order('created_at', { ascending: true });
        if (cancelled) return;
        if (messagesError) {
          console.warn('[web2 brain] message load failed', messagesError.message);
          setConversationId(conversationRow.id);
          setTurns([]);
          return;
        }

        setConversationId(conversationRow.id);
        setTurns(((messages ?? []) as MessageRow[]).map(rowToTurn).filter((turn): turn is BrainTurn => Boolean(turn)));
      } catch (err) {
        if (cancelled) return;
        console.warn('[web2 brain] persistence unavailable', err);
        setConversationId(null);
        setTurns([]);
      }
    })();

    return () => { cancelled = true; };
  }, [coachId, teamId]);

  const send = useCallback(async (text: string) => {
    if (!text.trim() || loading || !coachId) return;
    const cleanText = text.trim();

    // Pro-coach review mode short-circuit: if the coach is in review mode
    // AND the utterance is OBVIOUSLY meta-feedback about the app (per the
    // sync heuristic in coachFeedback.ts), skip the Brain tool loop and
    // reply with a brief acknowledgement. The review log captures the
    // feedback with kind='feedback' so Peter sees it on the admin feed.
    //
    // This deliberately runs ONLY on high-confidence matches — ambiguous
    // utterances flow through Brain normally and the async classifier
    // labels them after the fact (no behaviour change, just the label).
    if (reviewMode.isReviewMode && isObviousFeedback(cleanText)) {
      const ack = "Noted — logged that for Peter. Back to the session.";
      const userTurn: BrainTurn = { id: crypto.randomUUID(), role: 'user', text: cleanText };
      const ackTurn: BrainTurn = { id: crypto.randomUUID(), role: 'assistant', text: ack };
      setTurns((t) => [...t, userTurn, ackTurn]);
      void reviewMode.logKnownUtterance(cleanText, 'feedback', ack);
      return;
    }

    const userTurn: BrainTurn = { id: crypto.randomUUID(), role: 'user', text: cleanText };
    const streamingTurnId = crypto.randomUUID();
    const streamingTurn: BrainTurn = { id: streamingTurnId, role: 'assistant', text: '' };
    setTurns((t) => [...t, userTurn, streamingTurn]);
    setLoading(true);
    setStatus('Thinking…');

    // Offline path — no real Supabase / proxy configured. Skip persistence,
    // synthesize a canned session draft locally so the approve/dismiss UI
    // is exercisable end-to-end on `pnpm dev` without a backend.
    if (isPlaceholderSupabase()) {
      try {
        // Phase 8 verification: gather Layer-3 priors (player goals, growth
        // check-ins) so the offline reply visibly references them. Closes
        // the Layer 3 → Layer 2 loop locally.
        const priors = gatherOfflinePriors(coachId);
        // On the tactics surface, prefer the tactics synth so approving
        // the draft actually moves the board. Falls back to the session-
        // plan synth elsewhere.
        const { text: replyText, draft } =
          screen?.kind === 'tactics'
            ? synthesizeOfflineTacticsDraft(cleanText, screen.tacticsPersistKey ?? null)
            : synthesizeOfflineDraft(cleanText, teamId, priors);
        const draftTurn: BrainTurn = {
          id: crypto.randomUUID(),
          role: 'assistant',
          text: '',
          draft,
          status: 'pending',
        };
        setTurns((prev) => [
          ...prev.map((turn) =>
            turn.id === streamingTurnId ? { ...turn, text: replyText } : turn,
          ),
          draftTurn,
        ]);
      } finally {
        setLoading(false);
        setStatus('');
      }
      return;
    }

    try {
      let activeConversationId = conversationId;
      if (!activeConversationId) {
        const { data, error } = await supabase
          .from('coach_conversations')
          .insert({
            coach_id: coachId,
            team_id: teamId,
            title: cleanText.slice(0, 60),
          })
          .select('id')
          .single();
        if (error || !data) throw new Error(error?.message ?? 'could not start conversation');
        activeConversationId = (data as ConversationRow).id;
        setConversationId(activeConversationId);
      }

      const ctx = await buildBrainContext({
        coachId,
        teamId,
        screenContext: screen ?? DEFAULT_SCREEN,
        conversationId: activeConversationId,
        query: cleanText,
      });

      const userMessage = await supabase
        .from('coach_messages')
        .insert({
          conversation_id: activeConversationId,
          role: 'user',
          content: cleanText,
          context_snapshot: screen ?? DEFAULT_SCREEN,
        })
        .select('id')
        .single();
      if (userMessage.error) throw new Error(`could not save message: ${userMessage.error.message}`);

      const dispatch = makeBrainDispatcher({ coachId, teamId, getTacticsLayout });
      const result = await callAIToolsStreaming({
        system: ctx.system,
        messages: ctx.messages,
        tools: BRAIN_TOOLS,
        dispatch,
        maxTokens: 4000,
        onStatus: setStatus,
        surface: 'brain-chat',
        onTextDelta: (_delta, fullText) => {
          setTurns((prev) => prev.map((turn) =>
            turn.id === streamingTurnId ? { ...turn, text: fullText } : turn,
          ));
        },
      });

      const drafts = result.drafts ?? (result.draft ? [result.draft] : []);
      const finalText = result.finalText
        || (drafts.length > 0 ? `Drafted ${drafts.length} item${drafts.length === 1 ? '' : 's'} — review below.` : '(no response)');

      const toolCalls: SavedToolTrace[] = result.toolTrace.map((entry) => ({
        name: entry.name,
        input: entry.input,
        errored: entry.errored === true,
      }));
      const assistantRows: Array<{
        content: string;
        draftPayload: Record<string, unknown> | null;
        draftStatus: DraftStatus | null;
        toolCalls: SavedToolTrace[];
      }> = [
        {
          content: finalText,
          draftPayload: null,
          draftStatus: null,
          toolCalls,
        },
        ...drafts.map((draft) => ({
            content: '',
            draftPayload: serializeDraft(draft.type, draft.payload),
            draftStatus: 'pending' as DraftStatus,
            toolCalls: [],
          })),
      ];

      const draftTurns: BrainTurn[] = [];
      for (const row of assistantRows) {
        const { data, error } = await supabase
          .from('coach_messages')
          .insert({
            conversation_id: activeConversationId,
            role: 'assistant',
            content: row.content,
            tool_calls: row.toolCalls,
            context_snapshot: screen ?? DEFAULT_SCREEN,
            draft_payload: row.draftPayload,
            draft_status: row.draftStatus,
          })
          .select('id, created_at')
          .single();
        if (error) throw new Error(`could not save reply: ${error.message}`);
        const inserted = data as InsertedRow;
        if (row.draftPayload) {
          draftTurns.push({
            id: inserted.id,
            role: 'assistant',
            text: row.content,
            draft: parseDraft(row.draftPayload),
            status: row.draftStatus ?? 'pending',
          });
        } else {
          setTurns((prev) => prev.map((turn) =>
            turn.id === streamingTurnId ? { ...turn, id: inserted.id, text: row.content } : turn,
          ));
        }
      }

      if (draftTurns.length > 0) setTurns((t) => [...t, ...draftTurns]);
      await supabase
        .from('coach_conversations')
        .update({ last_message_at: new Date().toISOString() })
        .eq('id', activeConversationId);
    } catch (err) {
      console.warn('[web2 brain] send failed', err);
      const friendly = friendlyBrainError(err);
      setTurns((t) => t.map((turn) =>
        turn.id === streamingTurnId ? { ...turn, text: friendly } : turn,
      ));
    } finally {
      setLoading(false);
      setStatus('');
    }
  }, [coachId, teamId, screen, loading, conversationId, getTacticsLayout, reviewMode]);

  const approveDraft = useCallback(async (turnId: string) => {
    const turn = turns.find((t) => t.id === turnId);
    if (!turn?.draft) return;
    // Tactics drafts are pure client-side localStorage writes — they
    // don't need an authenticated coach. Server-side commits (sessions,
    // drills, principles, etc.) still require coachId for RLS.
    const isTacticsDraft = turn.draft.type === 'draft_tactics_pattern';
    const isPlayerBriefsDraft = turn.draft.type === 'draft_player_briefs';
    const isLocalOnlyDraft = isTacticsDraft || isPlayerBriefsDraft;
    if (!isLocalOnlyDraft && !coachId) return;
    if (isPlaceholderSupabase()) {
      // No backend to write server-side drafts to. Local-only drafts
      // (tactics + player-briefs) still need to run their commit
      // handlers because they hit localStorage on the active surface;
      // everything else just flips the status pill so the UI shows
      // "Saved" without a real round-trip.
      if (isTacticsDraft && onCommitTacticsDraft) {
        const tacticsRes = onCommitTacticsDraft(
          turn.draft.payload as import('./brainTactics').TacticsDraftPayload,
        );
        if (!tacticsRes.ok) {
          setTurns((prev) => [...prev, {
            id: crypto.randomUUID(),
            role: 'assistant',
            text: `Save failed: ${tacticsRes.error}`,
          }]);
          return;
        }
      }
      if (isPlayerBriefsDraft && onCommitPlayerBriefsDraft) {
        const briefsRes = onCommitPlayerBriefsDraft(
          turn.draft.payload as import('./brainTactics').PlayerBriefsDraftPayload,
        );
        if (!briefsRes.ok) {
          setTurns((prev) => [...prev, {
            id: crypto.randomUUID(),
            role: 'assistant',
            text: `Save failed: ${briefsRes.error}`,
          }]);
          return;
        }
      }
      setTurns((prev) => prev.map((t) => t.id === turnId ? { ...t, status: 'approved' } : t));
      return;
    }
    // Tactics drafts don't pass through commitDraft — they live in
    // localStorage on the surface that opened them. The surface (a
    // SessionTactics page) supplies onCommitTacticsDraft via
    // useTacticsCommitForBoard.
    let result: { ok: boolean; error?: string };
    if (turn.draft.type === 'draft_tactics_pattern') {
      if (!onCommitTacticsDraft) {
        result = { ok: false, error: 'no active tactics board to apply draft to' };
      } else {
        const tacticsRes = onCommitTacticsDraft(
          turn.draft.payload as import('./brainTactics').TacticsDraftPayload,
        );
        result = tacticsRes.ok ? { ok: true } : { ok: false, error: tacticsRes.error };
      }
    } else if (turn.draft.type === 'draft_player_briefs') {
      if (!onCommitPlayerBriefsDraft) {
        result = { ok: false, error: 'no active tactics board to apply briefs to' };
      } else {
        const briefsRes = onCommitPlayerBriefsDraft(
          turn.draft.payload as import('./brainTactics').PlayerBriefsDraftPayload,
        );
        result = briefsRes.ok ? { ok: true } : { ok: false, error: briefsRes.error };
      }
    } else {
      // Non-tactics drafts require a real coach for server-side RLS.
      // The guard at the top of approveDraft already returned early if
      // coachId is null on a non-tactics path; the `??` here keeps the
      // type system happy without re-asserting that fact.
      result = await commitDraft(turn.draft.type, turn.draft.payload, {
        teamId,
        coachId: coachId ?? undefined,
      });
    }
    if (!result.ok) {
      setTurns((prev) => [...prev, {
        id: crypto.randomUUID(),
        role: 'assistant',
        text: `Save failed: ${result.error ?? 'unknown error'}`,
      }]);
      return;
    }
    await supabase
      .from('coach_messages')
      .update({
        draft_status: 'approved',
        draft_payload: serializeDraft(turn.draft.type, turn.draft.payload),
      })
      .eq('id', turnId);
    setTurns((prev) => prev.map((t) => t.id === turnId ? { ...t, status: 'approved' } : t));
  }, [turns, coachId, teamId, onCommitTacticsDraft, onCommitPlayerBriefsDraft]);

  const dismissDraft = useCallback((turnId: string) => {
    if (!isPlaceholderSupabase()) {
      void supabase
        .from('coach_messages')
        .update({ draft_status: 'dismissed' })
        .eq('id', turnId);
    }
    setTurns((prev) => prev.map((t) => t.id === turnId ? { ...t, status: 'dismissed' } : t));
  }, []);

  return { turns, loading, status, send, approveDraft, dismissDraft };
}

/**
 * Computes per-page Brain Bar commands. Pages that need DO/ASK commands
 * call this with their context — handlers wire to send + navigate.
 */
export function useBrainCommands(send: (text: string) => void) {
  const navigate = useNavigate();
  return { send, navigate };
}
